import { call, put, takeLatest, take, select, fork, all } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { generateQRCode, scrollIntoView } from 'modules/app/actions';

import { simpleRequestsLoader } from 'modules/app/utils';
import * as coreActions from 'modules/interventions/actions/core';
import * as detailsActions from 'modules/interventions/actions/details';
import * as types from 'modules/interventions/actionTypes';
import { IMPORT_TIRE_REPORT_NOTIF } from 'modules/interventions/sagas/utils/utils';
import { getSelectedIntervention } from 'modules/interventions/selectors';
import { isInterventionToBeSent } from 'modules/interventions/selectors/base';
import { getSelectedInterventionChecklists } from 'modules/interventions/selectors/details';
import { createNotification } from 'modules/toast-notifications/actions';
import { getMediariesExportErrorNotification } from 'services/InterventionUtils';

import legacy from './core.legacy.sagas';
import dashboardSagas from './dashboard.saga';
import detailsSagas from './details.saga';
import errorSagas from './errors.saga';
import kanbanSagas from './kanban.saga';
import mobilitySagas from './mobility.saga';
import interventionSockets from './sockets.saga';
import { DROPDOWN_META_SOURCE } from '../constants/inspections';

/** @param {import('modules/interventions/types/actions').PrintFOQRCodeAction} action */
function* printFOQRCode({ payload }) {
  const { interventionId, mode, filename } = payload;

  try {
    yield put(detailsActions.shareInterventionLink({ interventionId, mode, shorten: true }, { silent: true }));
    const { type, payload: linkPayload } = yield take([
      types.FETCH_INTERVENTION_LINK_SUCCESS,
      types.FETCH_INTERVENTION_LINK_FAILURE
    ]);

    if (type === types.FETCH_INTERVENTION_LINK_FAILURE) {
      throw new Error(linkPayload);
    }

    yield put(generateQRCode({ url: linkPayload, filename }));
  // eslint-disable-next-line no-unused-vars
  } catch (error) {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.print_fo_qr_code.failure',
          defaultMessage: 'Can’t download the QR Code'
        }
      })
    );
  }
}

function* importTscansOrAudits({ payload, meta }) {
  const { request, entity } = payload;
  const importedIds = request.data.ids;
  try {
    const { data: intervention } = yield call(restClient.request, request);
    yield put(detailsActions.importAuditsOrTscansSuccess(intervention, importedIds, meta));
    if (meta?.source !== DROPDOWN_META_SOURCE) {
      yield put(scrollIntoView('#pending-works'));
    }
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: `page.fiche.notification.import_${entity}.success`,
          defaultMessage: `${entity} report imported`,
          values: { count: importedIds.length }
        }
      })
    );
  } catch (error) {
    yield put(detailsActions.importAuditsOrTscansFailure(error, { ...meta, entityIds: importedIds }));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: `page.fiche.notification.import_${entity}.failure`,
          defaultMessage: `Can't import the ${entity} report`,
          values: { count: importedIds.length }
        }
      })
    );
  }
}

function* openSendModal() {
  const { id: interventionId, mode } = yield select(getSelectedIntervention);
  const isToBeSent = yield select(isInterventionToBeSent, interventionId);
  if (!isToBeSent) {
    yield put(detailsActions.shareInterventionLink({ interventionId, mode, shorten: true }));
  }
}

function* getWorksOrder({ payload }) {
  const { request, interventionId } = payload;

  try {
    const response = yield call(restClient.request, request);
    const data = { workOrderWorks: response.data.content };
    const checklists = yield select(getSelectedInterventionChecklists);
    if (data.workOrderWorks.length === 0) {
      yield put(coreActions.fetchWorkByWorkOrderFailure());
    } else {
      yield put(coreActions.fetchWorkByWorkOrderSuccess(interventionId, data, checklists));
    }
  } catch (message) {
    yield put(coreActions.fetchWorkByWorkOrderFailure(message));
  }
}

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.INTERVENTIONS_FETCH]: {},
  [types.APPROVE_MANUALLY_REQUEST]: {},
  [types.REBRAND_REQUEST]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.update_intervention.success',
        defaultMessage: 'File updated'
      }
    }
  },
  [types.UPDATE_INTERVENTION_TAGS]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.update_tags.success',
        defaultMessage: 'The file tags have been updated with success.'
      },
      failure: {
        id: 'page.fiche.notification.update_tags.error',
        defaultMessage: 'An error occurred while updating the file tags.'
      }
    }
  },
  [types.EVALUATE_INTERVENTION_TAGS]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.refresh_tags.success',
        defaultMessage: 'The file tags have been refreshed successfully.'
      },
      failure: {
        id: 'page.fiche.notification.refresh_tags.error',
        defaultMessage: 'An error occurred while refreshing the file tags.'
      }
    }
  },
  [types.IMPORT_CHECKLISTS]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.import_checklist.success',
        defaultMessage: 'Checklist imported successfully'
      },
      failure: {
        id: 'page.fiche.notification.import_checklist.failure',
        defaultMessage: "Can't import checklist"
      }
    }
  },
  [types.IMPORT_WORKS_INTO_INTERVENTION]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.works_import_from_work_order.success',
        defaultMessage: 'Work imported successfully'
      },
      failure: {
        id: 'page.fiche.notification.works_import_from_work_order.failure',
        defaultMessage: "Can't import work from work order"
      }
    }
  },
  [types.IMPORT_TIRE_REPORT]: {
    notifications: {
      success: ({ payload: { mode } }) => IMPORT_TIRE_REPORT_NOTIF[mode]
    }
  },
  [types.INTERVENTION_INVOICE_PATCH_STATUS]: {
    notifications: {
      success: (_, action) => ({
        id: `final_invoice.patch.${action.payload.request.data.status.toLowerCase()}`,
        defaultMessage: 'Invoice marked as paid with success.'
      })
    }
  },
  [types.MEDIARIES_EXPORT]: {
    callbacks: {
      success: (_, meta, { payload }) => detailsActions.mediariesExportSuccess(payload.request.data.integrations, meta),
      failure: (error, meta, { payload }) =>
        detailsActions.mediariesExportFailure(error, payload.request.data.integrations, meta)
    },
    notifications: {
      success: {
        id: 'page.fiche.infomediaries.refreshInProgress',
        defaultMessage: 'Media export started, this operation may take a few minutes.'
      },
      failure: getMediariesExportErrorNotification
    }
  },
  [types.FETCH_LINKED_INTERVENTIONS_REQUEST]: {
    callbacks: {
      success: data => detailsActions.fetchLinkedInterventionsSuccess(data.sameReferenceInterventions),
      failure: error => detailsActions.fetchLinkedInterventionsFailure(error)
    }
  },
  [types.IMPORT_CHECKLISTS_GROUPS]: {
    notifications: {
      success: (_, action) => {
        const leaf = action.meta?.target === 'intervention-import' ? 'successStock' : 'success';
        return {
          id: `page.fiche.addOfferModal.import.${leaf}`,
          defaultMessage: 'Offer successfully created.'
        };
      },
      failure: {
        id: 'page.fiche.addOfferModal.import.error',
        defaultMessage: 'An error occurred during the import process.'
      }
    }
  },
};

export default function* interventionsSagas() {
  yield fork(legacy);

  // Simple requests
  yield all(simpleRequestsLoader(simpleRequests));

  // Core
  yield takeLatest(types.OPEN_SEND_MODAL, openSendModal);
  yield takeLatest(types.PRINT_FO_QR_CODE, printFOQRCode);
  yield takeLatest(types.IMPORT_AUDITS_OR_TSCANS, importTscansOrAudits);
  yield takeLatest(types.GET_WORKS_ORDER_BY_REPARATION_ORDER, getWorksOrder);

  // Dashboards
  yield fork(kanbanSagas);
  yield fork(dashboardSagas);

  // Details
  yield fork(detailsSagas);
  yield fork(interventionSockets);

  // Others
  yield fork(mobilitySagas);
  yield fork(errorSagas);
}
